<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">Invoices</h1>
            <p class="text-muted mb-0">
              Bill your customers and track what they have paid
            </p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              title="Refresh"
              :disabled="loading"
              @click="refreshAll"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Create invoice
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Desktop View: Tabs -->
    <div class="d-none d-lg-block">
      <CTabs :active-tab="0" variant="tabs" add-tab-classes="mb-3">
        <!-- Tab 1: Analytics -->
        <CTab>
          <template slot="title">
            <CIcon name="cil-chart-line" class="mr-1" /> Analytics
          </template>

          <!-- Invoice Insights -->
          <CRow class="mb-4">
            <!-- Invoice Summary -->
            <CCol lg="8">
        <CCard class="h-100 border-0">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h5 mb-1">Invoice summary</h2>
            <small class="text-muted">All approved invoices; the chart shows the last 6 months</small>
          </CCardHeader>
          <CCardBody class="pt-0 pb-4 px-4">
            <CRow class="mb-4">
              <CCol sm="6" md="3" class="mb-3 mb-md-0">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Total invoices</span>
                    <span class="inv-kpi-chip chip-navy"><CIcon name="cil-file" /></span>
                  </div>
                  <div class="inv-kpi-value">{{ invoiceSummary.totalCount }}</div>
                  <div class="inv-kpi-sub" :class="invoiceSummary.countChange >= 0 ? 'trend-up' : 'trend-down'">
                    <CIcon :name="invoiceSummary.countChange >= 0 ? 'cil-arrow-top' : 'cil-arrow-bottom'" />
                    {{ Math.abs(invoiceSummary.countChange) }}% vs last month
                  </div>
                </div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3 mb-md-0">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Paid</span>
                    <span class="inv-kpi-chip chip-success"><CIcon name="cil-check-circle" /></span>
                  </div>
                  <div class="inv-kpi-value">{{ formatCurrency(invoiceSummary.paidAmount) }}</div>
                  <div class="inv-kpi-sub">{{ invoiceSummary.paidCount }} invoice{{ invoiceSummary.paidCount === 1 ? '' : 's' }}</div>
                </div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3 mb-md-0">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Outstanding</span>
                    <span class="inv-kpi-chip chip-warning"><CIcon name="cil-clock" /></span>
                  </div>
                  <div class="inv-kpi-value">{{ formatCurrency(invoiceSummary.pendingAmount) }}</div>
                  <div class="inv-kpi-sub">{{ invoiceSummary.pendingCount }} invoice{{ invoiceSummary.pendingCount === 1 ? '' : 's' }}</div>
                </div>
              </CCol>
              <CCol sm="6" md="3">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Overdue</span>
                    <span class="inv-kpi-chip chip-danger"><CIcon name="cil-warning" /></span>
                  </div>
                  <div class="inv-kpi-value">{{ formatCurrency(invoiceSummary.overdueAmount) }}</div>
                  <div class="inv-kpi-sub">{{ invoiceSummary.overdueCount }} invoice{{ invoiceSummary.overdueCount === 1 ? '' : 's' }}</div>
                </div>
              </CCol>
            </CRow>
            <InvoiceTrendChart
              :chartData="invoiceTrendData"
              style="height: 250px"
            />
          </CCardBody>
        </CCard>
      </CCol>

      <!-- Status Distribution -->
      <CCol lg="4">
        <CCard class="h-100 border-0">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h5 mb-1">Status distribution</h2>
            <small class="text-muted">All invoices, by status</small>
          </CCardHeader>
          <CCardBody
            class="pt-0 pb-4 px-4 d-flex flex-column align-items-center justify-content-center"
          >
            <div style="height: 200px; width: 200px" class="mb-4">
              <InvoiceStatusChart :chartData="statusDistributionData" />
            </div>
            <div class="w-100">
              <div
                v-for="status in statusDistribution"
                :key="status.name"
                class="d-flex align-items-center mb-2"
              >
                <CBadge :color="status.color" class="mr-2 px-2 py-1">{{ status.label }}</CBadge>
                <div class="flex-grow-1"></div>
                <div class="text-right">
                  <strong>{{ status.count }}</strong>
                  <span class="text-muted ml-1"
                    >({{ status.percentage }}%)</span
                  >
                </div>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
        </CTab>

        <!-- Tab 2: All Invoices -->
        <CTab>
          <template slot="title">
            <CIcon name="cil-list" class="mr-1" /> All invoices
            <CBadge color="light" class="ml-2">{{ totalItems }}</CBadge>
          </template>

    <!-- Invoice List -->
    <CCard class="mb-4 border-0">
      <CCardHeader class="bg-transparent border-0 p-4">
        <h2 class="h5 mb-0">
          Invoice list
          <CBadge color="light" class="ml-2">{{ totalItems }}</CBadge>
        </h2>
      </CCardHeader>
      <CCardBody class="p-4">
        <!-- Advanced Filters -->
        <CCollapse :show="showFilters">
          <CCard class="mb-4 border">
            <CCardBody>
              <CRow>
                <CCol md="3">
                  <CInput
                    label="Invoice number"
                    v-model="filters.invoiceNumber"
                    placeholder="e.g. INV-0012"
                    @keyup.enter="applyFilters"
                  />
                </CCol>
                <CCol md="3">
                  <div class="form-group">
                    <label class="font-weight-bold">Customer</label>
                    <v-select
                      v-model="selectedCustomerFilter"
                      :options="customerOptions"
                      label="name"
                      placeholder="Select customer"
                      :clearable="true"
                      @input="onCustomerFilterChange"
                    >
                      <template v-slot:option="option">
                        <div>
                          <div class="font-weight-bold">{{ option.name }}</div>
                          <small v-if="option.email" class="text-muted">{{ option.email }}</small>
                        </div>
                      </template>
                      <template v-slot:selected-option="option">
                        <span>{{ option.name }}</span>
                      </template>
                    </v-select>
                  </div>
                </CCol>
                <CCol md="3">
                  <CSelect
                    label="Status"
                    v-model="filters.status"
                    @change="applyFilters"
                  >
                    <option value="">All statuses</option>
                    <option
                      v-for="status in statusOptions"
                      :key="status.id"
                      :value="status.name"
                    >
                      {{ badgeLabel("invoice", status.name) }}
                    </option>
                  </CSelect>
                </CCol>
                <CCol md="3">
                  <CInput
                    type="date"
                    label="From"
                    v-model="filters.dateFrom"
                    class="mb-3"
                    @change="applyFilters"
                  />
                  <CInput
                    type="date"
                    label="To"
                    v-model="filters.dateTo"
                    @change="applyFilters"
                  />
                </CCol>
              </CRow>
              <CRow class="mt-3">
                <CCol class="d-flex justify-content-end">
                  <CButton color="light" class="mr-2" @click="resetFilters">
                    <CIcon name="cil-x" /> Reset
                  </CButton>
                  <CButton color="primary" @click="applyFilters">
                    <CIcon name="cil-filter" /> Apply filters
                  </CButton>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCollapse>

        <!-- Quick Period Filter (Desktop) -->
        <div class="d-none d-md-flex flex-wrap align-items-center mb-3 pb-3 border-bottom">
          <small class="text-muted font-weight-bold mr-3">Quick filter</small>
          <CButtonGroup size="sm" class="mr-3">
            <CButton
              color="primary"
              :variant="filters.quickPeriod === 'all' ? '' : 'outline'"
              @click="applyQuickFilter('all')"
            >
              All time
            </CButton>
            <CButton
              color="primary"
              :variant="filters.quickPeriod === 'thisMonth' ? '' : 'outline'"
              @click="applyQuickFilter('thisMonth')"
            >
              This month
            </CButton>
            <CButton
              color="primary"
              :variant="filters.quickPeriod === 'lastMonth' ? '' : 'outline'"
              @click="applyQuickFilter('lastMonth')"
            >
              Last month
            </CButton>
            <CButton
              color="primary"
              :variant="filters.quickPeriod === 'thisQuarter' ? '' : 'outline'"
              @click="applyQuickFilter('thisQuarter')"
            >
              This quarter
            </CButton>
            <CButton
              color="primary"
              :variant="filters.quickPeriod === 'thisYear' ? '' : 'outline'"
              @click="applyQuickFilter('thisYear')"
            >
              This year
            </CButton>
          </CButtonGroup>
          <small class="text-muted mr-2">or</small>
          <CSelect
            v-model="filters.selectedMonthYear"
            @change="applyMonthYearFilter"
            size="sm"
            style="width: 180px"
          >
            <option value="">Choose a month</option>
            <option
              v-for="month in monthYearOptions"
              :key="month.value"
              :value="month.value"
            >
              {{ month.label }}
            </option>
          </CSelect>
        </div>

        <!-- Quick Period Filter (Mobile) -->
        <div class="d-md-none mb-3">
          <CRow>
            <CCol xs="12" class="mb-2">
              <CSelect
                v-model="filters.quickPeriod"
                @change="applyQuickFilter(filters.quickPeriod)"
                size="sm"
              >
                <option value="all">All time</option>
                <option value="thisMonth">This month</option>
                <option value="lastMonth">Last month</option>
                <option value="thisQuarter">This quarter</option>
                <option value="thisYear">This year</option>
              </CSelect>
            </CCol>
            <CCol xs="12">
              <CSelect
                v-model="filters.selectedMonthYear"
                @change="applyMonthYearFilter"
                size="sm"
              >
                <option value="">Or choose a month</option>
                <option
                  v-for="month in monthYearOptions"
                  :key="month.value"
                  :value="month.value"
                >
                  {{ month.label }}
                </option>
              </CSelect>
            </CCol>
          </CRow>
        </div>

        <!-- Filter Toggle -->
        <CRow class="mb-4 align-items-center">
          <CCol xs="12" class="d-flex align-items-center flex-wrap">
            <CButton color="light" :aria-expanded="showFilters ? 'true' : 'false'" @click="showFilters = !showFilters">
              <CIcon :name="showFilters ? 'cil-chevron-top' : 'cil-filter'" />
              {{ showFilters ? "Hide filters" : "Show filters" }}
            </CButton>
            <template v-if="hasActiveFilters">
              <CBadge color="primary" class="ml-2">
                {{ activeFiltersCount }} active
              </CBadge>
              <CButton color="link" size="sm" class="ml-1" @click="resetFilters">
                Clear filters
              </CButton>
            </template>
          </CCol>
        </CRow>

        <!-- Filtered Results Summary -->
        <CCard v-if="!loading && computedItems.length > 0" class="mb-3 inv-summary-card">
          <CCardBody class="p-3">
            <div class="d-flex align-items-center mb-3">
              <CIcon name="cil-calculator" class="mr-2 text-brand-navy" />
              <span class="inv-summary-title">Filtered results summary</span>
              <CBadge class="ml-2 badge-soft">{{ filteredSummary.totalCount }} invoice{{ filteredSummary.totalCount !== 1 ? 's' : '' }}</CBadge>
            </div>
            <CRow>
              <CCol lg="2" md="4" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Before tax</div><div class="inv-stat-val">{{ formatCurrency(filteredSummary.subTotal) }}</div></div>
              </CCol>
              <CCol lg="2" md="4" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Tax (SST)</div><div class="inv-stat-val text-warn">{{ formatCurrency(filteredSummary.taxAmount) }}</div></div>
              </CCol>
              <CCol lg="2" md="4" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Grand total</div><div class="inv-stat-val text-brand-navy">{{ formatCurrency(filteredSummary.totalAmount) }}</div></div>
              </CCol>
              <CCol lg="2" md="4" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Paid</div><div class="inv-stat-val text-success-strong">{{ formatCurrency(filteredSummary.paidAmount) }}</div><div class="inv-stat-meta">{{ filteredSummary.paidCount }} inv</div></div>
              </CCol>
              <CCol lg="2" md="4" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Unpaid</div><div class="inv-stat-val text-danger-strong">{{ formatCurrency(filteredSummary.unpaidAmount) }}</div><div class="inv-stat-meta">{{ filteredSummary.unpaidCount }} inv</div></div>
              </CCol>
              <CCol lg="2" md="4" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Average</div><div class="inv-stat-val">{{ formatCurrency(filteredSummary.averageAmount) }}</div><div class="inv-stat-meta">per invoice</div></div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <!-- Desktop View with Enhanced Data Table -->
        <div class="d-none d-lg-block">
          <div v-if="loading && !items.length" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading invoices</span>
            </div>
            <p class="mt-3 text-muted">Loading invoices</p>
          </div>

          <!-- Error (never falls through to the empty state) -->
          <div v-else-if="loadError" class="text-center py-5" role="alert">
            <CIcon name="cil-warning" size="2xl" class="mb-3 text-danger" />
            <h3 class="h5">Couldn't load invoices</h3>
            <p class="text-muted mb-3">Check your connection and try again.</p>
            <CButton color="primary" @click="refreshTable">
              <CIcon name="cil-reload" /> Try again
            </CButton>
          </div>

          <CDataTable
            v-else
            :items="computedItems"
            :fields="fields"
            :loading="loading"
            :pagination="{
              align: 'end',
              doubleArrows: true
            }"
            :sorter="{ external: true }"
            :table-filter="false"
            :items-per-page-select="{
              values: [10, 25, 50, 100],
              label: 'Items per page:'
            }"
            :items-per-page="perPage"
            :active-page="currentPage"
            :pages="totalPages"
            hover
            striped
            bordered
            responsive
            @pagination-change="onPaginationChange"
            @items-per-page-change="onItemsPerPageChange"
            @sorter-value-change="onSorterChange"
          >
            <!-- Index Column -->
            <template #show_index="{ index }">
              <td class="py-2 text-muted">
                {{ (currentPage - 1) * perPage + index + 1 }}
              </td>
            </template>

            <!-- Invoice Number Column -->
            <template #invoiceNumber="{ item }">
              <td class="py-2">
                <router-link
                  :to="`/tenants/Invoice/${item.id}`"
                  class="font-weight-bold text-monospace"
                >
                  {{ item.invoiceNumber }}
                </router-link>
              </td>
            </template>

            <!-- Customer Column -->
            <template #customerName="{ item }">
              <td class="py-2">
                <div class="font-weight-medium">{{ item.customerName }}</div>
              </td>
            </template>

            <!-- Date Column with Better Formatting -->
            <template #displayIssuedDate="{ item }">
              <td class="py-2">
                <div>{{ item.displayIssuedDate }}</div>
                <small v-if="item.dueDate" class="text-muted">Due {{ getDisplayDate(item.dueDate) }}</small>
              </td>
            </template>

            <!-- Before Tax Column -->
            <template #subTotal="{ item }">
              <td class="py-2 text-right">
                <div class="text-muted">
                  {{ formatCurrency(item.subTotal) }}
                </div>
              </td>
            </template>

            <!-- Tax Amount Column -->
            <template #taxAmount="{ item }">
              <td class="py-2 text-right">
                <div class="font-weight-medium">
                  {{ formatCurrency(item.taxAmount) }}
                </div>
                <small class="text-muted" v-if="item.taxPercentage">
                  ({{ item.taxPercentage }}%)
                </small>
              </td>
            </template>

            <!-- Total Amount Column -->
            <template #grandTotal="{ item }">
              <td class="py-2 text-right">
                <div class="font-weight-bold text-dark text-nowrap">
                  {{ formatCurrency(item.grandTotal) }}
                </div>
              </td>
            </template>

            <!-- Status Column -->
            <template #show_status="{ item }">
              <td class="py-2">
                <CBadge
                  :color="badgeColor('invoice', item.statusDescription)"
                  class="px-2 py-1"
                >
                  {{ badgeLabel("invoice", item.statusDescription) }}
                </CBadge>
              </td>
            </template>

            <!-- Payment Status Column -->
            <template #show_payment_status="{ item }">
              <td class="py-2">
                <div v-if="payState(item)">
                  <CBadge :color="badgeColor('documentPayment', payState(item))" class="px-2 py-1 mb-1">
                    {{ badgeLabel("documentPayment", payState(item)) }}
                  </CBadge>
                  <template v-if="item.isPaid">
                    <div class="small text-muted" v-if="item.paymentDate">
                      {{ getDisplayDate(item.paymentDate) }}
                    </div>
                    <div class="small text-muted" v-if="item.paymentMethod">
                      via {{ item.paymentMethod }}
                    </div>
                  </template>
                </div>
                <div v-else>
                  <span class="text-muted">—</span>
                </div>
              </td>
            </template>

            <!-- Actions Column -->
            <template #show_details="{ item }">
              <td class="py-2 position-relative">
                <div class="text-right action-buttons">
                  <CButtonGroup>
                    <CButton
                      color="light"
                      size="sm"
                      @click="onEdit(item)"
                      title="Open invoice"
                      :aria-label="`Open invoice ${item.invoiceNumber}`"
                      class="border"
                    >
                      <CIcon name="cil-pencil" />
                    </CButton>
                    <CDropdown
                      size="sm"
                      color="light"
                      placement="bottom-end"
                      class="dropdown-with-zindex border"
                      :aria-label="`More actions for invoice ${item.invoiceNumber}`"
                    >
                      <template #toggler-content>
                        <CIcon name="cil-options" />
                        <span class="sr-only">More actions</span>
                      </template>
                      <CDropdownItem @click="onEdit(item)">
                        <CIcon name="cil-pencil" class="mr-2" /> Open
                      </CDropdownItem>
                      <CDropdownItem @click="onDuplicate(item)">
                        <CIcon name="cil-copy" class="mr-2" /> Duplicate
                      </CDropdownItem>
                      <CDropdownItem @click="onEdit(item)">
                        <CIcon name="cil-cloud-download" class="mr-2" /> Preview PDF
                      </CDropdownItem>
                      <CDropdownDivider />
                      <CDropdownItem
                        @click="showDeleteConfirmation(item)"
                        class="text-danger"
                      >
                        <CIcon name="cil-trash" class="mr-2" /> Delete
                      </CDropdownItem>
                    </CDropdown>
                  </CButtonGroup>
                </div>
              </td>
            </template>

            <!-- Empty states: filtered vs first use -->
            <template #no-items-view>
              <div v-if="hasActiveFilters" class="text-center py-5">
                <CIcon name="cil-filter" size="2xl" class="mb-3 text-secondary" />
                <h3 class="h5">No invoices match your filters</h3>
                <CButton color="light" class="mt-2" @click="resetFilters">
                  <CIcon name="cil-x" /> Clear filters
                </CButton>
              </div>
              <div v-else class="text-center py-5">
                <CIcon name="cil-inbox" size="2xl" class="mb-3 text-secondary" />
                <h3 class="h5">No invoices yet</h3>
                <p class="text-muted mb-3">
                  Create an invoice to bill a customer. It stays a draft until you approve it.
                </p>
                <CButton color="primary" @click="addNew">
                  <CIcon name="cil-plus" /> Create invoice
                </CButton>
              </div>
            </template>
          </CDataTable>

          <!-- Pagination Display -->
          <CRow class="mt-2" v-if="!loading && totalItems > 0">
            <CCol md="6" class="text-muted text-center">
              <small>
                Showing {{ paginationStart }}–{{ paginationEnd }} of {{ totalItems }}
                {{ totalItems === 1 ? "invoice" : "invoices" }}
                <span v-if="hasActiveFilters">(filtered)</span>
              </small>
            </CCol>
            <CCol md="6" class="text-center">
              <CPagination
                v-if="totalPages > 1"
                :activePage="currentPage"
                :pages="totalPages"
                @update:activePage="onPageChange"
                size="sm"
                class="mb-0"
              />
            </CCol>
          </CRow>
        </div>

      </CCardBody>
    </CCard>
        </CTab>
      </CTabs>
    </div>

    <!-- Mobile View: Collapsible Analytics + Invoice List -->
    <div class="d-lg-none">
      <!-- Collapsible Analytics Card -->
      <CCard class="mb-3 border-0">
        <button
          type="button"
          class="btn btn-block text-left bg-light p-3 m-0 rounded-0"
          :aria-expanded="mobileAnalyticsExpanded ? 'true' : 'false'"
          @click="toggleMobileAnalytics"
        >
          <CRow class="align-items-center">
            <CCol>
              <small class="text-muted d-block mb-2">Quick stats</small>
              <div class="d-flex justify-content-between small">
                <span><strong>{{ invoiceSummary.totalCount }}</strong> <small class="text-muted">Total</small></span>
                <span><strong>{{ formatCurrency(invoiceSummary.paidAmount) }}</strong> <small class="text-muted">Paid</small></span>
                <span><strong>{{ formatCurrency(invoiceSummary.pendingAmount) }}</strong> <small class="text-muted">Outstanding</small></span>
              </div>
            </CCol>
            <CCol xs="auto">
              <CIcon
                :name="mobileAnalyticsExpanded ? 'cil-chevron-top' : 'cil-chevron-bottom'"
                size="lg"
                class="text-primary"
              />
            </CCol>
          </CRow>
        </button>
        <CCollapse :show="mobileAnalyticsExpanded">
          <CCardBody class="p-3">
            <!-- Compact Summary Metrics -->
            <CRow class="mb-3">
              <CCol xs="6" class="mb-2">
                <CCallout color="info" class="mb-0 p-2">
                  <div class="text-muted small">Total invoices</div>
                  <div class="font-weight-bold h5 mb-0">{{ invoiceSummary.totalCount }}</div>
                </CCallout>
              </CCol>
              <CCol xs="6" class="mb-2">
                <CCallout color="success" class="mb-0 p-2">
                  <div class="text-muted small">Paid</div>
                  <div class="font-weight-bold h5 mb-0">{{ formatCurrency(invoiceSummary.paidAmount) }}</div>
                  <small class="text-muted">{{ invoiceSummary.paidCount }} {{ invoiceSummary.paidCount === 1 ? "invoice" : "invoices" }}</small>
                </CCallout>
              </CCol>
              <CCol xs="6" class="mb-2">
                <CCallout color="warning" class="mb-0 p-2">
                  <div class="text-muted small">Outstanding</div>
                  <div class="font-weight-bold h5 mb-0">{{ formatCurrency(invoiceSummary.pendingAmount) }}</div>
                  <small class="text-muted">{{ invoiceSummary.pendingCount }} {{ invoiceSummary.pendingCount === 1 ? "invoice" : "invoices" }}</small>
                </CCallout>
              </CCol>
              <CCol xs="6" class="mb-2">
                <CCallout color="danger" class="mb-0 p-2">
                  <div class="text-muted small">Overdue</div>
                  <div class="font-weight-bold h5 mb-0">{{ formatCurrency(invoiceSummary.overdueAmount) }}</div>
                  <small class="text-danger">{{ invoiceSummary.overdueCount }} {{ invoiceSummary.overdueCount === 1 ? "invoice" : "invoices" }}</small>
                </CCallout>
              </CCol>
            </CRow>

            <!-- Mobile Status Distribution -->
            <div class="text-center">
              <h3 class="h6 mb-3">Status distribution</h3>
              <div style="height: 200px; width: 200px; margin: 0 auto;">
                <InvoiceStatusChart :chartData="statusDistributionData" />
              </div>
              <div class="mt-3">
                <div
                  v-for="status in statusDistribution"
                  :key="status.name"
                  class="d-flex align-items-center justify-content-between mb-2"
                >
                  <CBadge :color="status.color" class="px-2 py-1">{{ status.label }}</CBadge>
                  <div>
                    <strong>{{ status.count }}</strong>
                    <span class="text-muted ml-1">({{ status.percentage }}%)</span>
                  </div>
                </div>
              </div>
            </div>
          </CCardBody>
        </CCollapse>
      </CCard>

      <!-- Mobile Invoice List -->
      <CCard class="mb-4 border-0">
        <CCardHeader class="bg-transparent border-0 p-3">
          <CRow class="align-items-center">
            <CCol>
              <h2 class="h5 mb-0">
                Invoice list
                <CBadge color="light" class="ml-2">{{ totalItems }}</CBadge>
              </h2>
            </CCol>
          </CRow>
        </CCardHeader>
        <CCardBody class="p-3">
          <!-- Filters Toggle -->
          <CRow class="mb-3 align-items-center">
            <CCol xs="12">
              <CButton
                color="light"
                block
                :aria-expanded="showFilters ? 'true' : 'false'"
                @click="showFilters = !showFilters"
                class="d-flex align-items-center justify-content-between"
              >
                <span>
                  <CIcon :name="showFilters ? 'cil-chevron-top' : 'cil-filter'" />
                  {{ showFilters ? "Hide filters" : "Show filters" }}
                </span>
                <CBadge v-if="hasActiveFilters" color="primary">
                  {{ activeFiltersCount }}
                </CBadge>
              </CButton>
            </CCol>
          </CRow>

          <!-- Mobile Filters (reuse existing filter section) -->
          <CCollapse :show="showFilters">
            <CCard class="mb-3 border">
              <CCardBody class="p-2">
                <CRow>
                  <CCol xs="12" class="mb-2">
                    <CInput
                      label="Invoice number"
                      v-model="filters.invoiceNumber"
                      placeholder="e.g. INV-0012"
                      @keyup.enter="applyFilters"
                      size="sm"
                    />
                  </CCol>
                  <CCol xs="12" class="mb-2">
                    <div class="form-group mb-0">
                      <label class="font-weight-bold small">Customer</label>
                      <v-select
                        v-model="selectedCustomerFilter"
                        :options="customerOptions"
                        label="name"
                        placeholder="Select customer"
                        :clearable="true"
                        @input="onCustomerFilterChange"
                      >
                        <template v-slot:option="option">
                          <div>
                            <div class="font-weight-bold">{{ option.name }}</div>
                            <small v-if="option.email" class="text-muted">{{ option.email }}</small>
                          </div>
                        </template>
                      </v-select>
                    </div>
                  </CCol>
                  <CCol xs="6" class="mb-2">
                    <CSelect
                      label="Status"
                      v-model="filters.status"
                      @change="applyFilters"
                      size="sm"
                    >
                      <option value="">All</option>
                      <option
                        v-for="status in statusOptions"
                        :key="status.id"
                        :value="status.name"
                      >
                        {{ badgeLabel("invoice", status.name) }}
                      </option>
                    </CSelect>
                  </CCol>
                  <CCol xs="6" class="mb-2">
                    <CInput
                      type="date"
                      label="From"
                      v-model="filters.dateFrom"
                      @change="applyFilters"
                      size="sm"
                    />
                  </CCol>
                  <CCol xs="6" class="mb-2">
                    <CInput
                      type="date"
                      label="To"
                      v-model="filters.dateTo"
                      @change="applyFilters"
                      size="sm"
                    />
                  </CCol>
                </CRow>
                <CRow class="mt-2">
                  <CCol xs="6">
                    <CButton color="light" size="sm" block @click="resetFilters">
                      <CIcon name="cil-x" size="sm" /> Reset
                    </CButton>
                  </CCol>
                  <CCol xs="6">
                    <CButton color="primary" size="sm" block @click="applyFilters">
                      <CIcon name="cil-filter" size="sm" /> Apply
                    </CButton>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>
          </CCollapse>

          <!-- Mobile Filtered Summary -->
          <CCard v-if="!loading && computedItems.length > 0" class="mb-3 inv-summary-card">
            <CCardBody class="p-2">
              <div class="d-flex align-items-center mb-2">
                <CIcon name="cil-calculator" size="sm" class="mr-1 text-brand-navy" />
                <span class="inv-summary-title small">Summary</span>
                <CBadge class="ml-1 badge-soft">{{ filteredSummary.totalCount }}</CBadge>
              </div>
              <CRow class="text-center">
                <CCol xs="4" class="mb-2"><div class="inv-stat-mini"><div class="inv-stat-label">Before tax</div><div class="inv-stat-val">{{ formatCurrency(filteredSummary.subTotal) }}</div></div></CCol>
                <CCol xs="4" class="mb-2"><div class="inv-stat-mini"><div class="inv-stat-label">Tax</div><div class="inv-stat-val text-warn">{{ formatCurrency(filteredSummary.taxAmount) }}</div></div></CCol>
                <CCol xs="4" class="mb-2"><div class="inv-stat-mini"><div class="inv-stat-label">Total</div><div class="inv-stat-val text-brand-navy">{{ formatCurrency(filteredSummary.totalAmount) }}</div></div></CCol>
                <CCol xs="4"><div class="inv-stat-mini"><div class="inv-stat-label">Paid</div><div class="inv-stat-val text-success-strong">{{ formatCurrency(filteredSummary.paidAmount) }}</div></div></CCol>
                <CCol xs="4"><div class="inv-stat-mini"><div class="inv-stat-label">Unpaid</div><div class="inv-stat-val text-danger-strong">{{ formatCurrency(filteredSummary.unpaidAmount) }}</div></div></CCol>
                <CCol xs="4"><div class="inv-stat-mini"><div class="inv-stat-label">Average</div><div class="inv-stat-val">{{ formatCurrency(filteredSummary.averageAmount) }}</div></div></CCol>
              </CRow>
            </CCardBody>
          </CCard>

          <!-- Mobile Loading State -->
          <div v-if="loading && !items.length" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading invoices</span>
            </div>
            <p class="mt-3 text-muted">Loading invoices</p>
          </div>

          <!-- Mobile Error State (never shows the empty state) -->
          <div v-else-if="loadError" class="text-center py-5" role="alert">
            <CIcon name="cil-warning" size="2xl" class="mb-3 text-danger" />
            <h3 class="h5">Couldn't load invoices</h3>
            <p class="text-muted mb-3">Check your connection and try again.</p>
            <CButton color="primary" @click="refreshTable">
              <CIcon name="cil-reload" /> Try again
            </CButton>
          </div>

          <!-- Mobile Invoice Cards -->
          <div v-else>
            <!-- Items per page selector -->
            <CRow class="mb-3" v-if="computedItems.length > 0">
              <CCol xs="6">
                <CSelect v-model="perPage" @change="onItemsPerPageChange" size="sm">
                  <option value="10">10 per page</option>
                  <option value="25">25 per page</option>
                  <option value="50">50 per page</option>
                </CSelect>
              </CCol>
              <CCol xs="6" class="text-right text-muted d-flex align-items-center justify-content-end small">
                {{ paginationStart }}-{{ paginationEnd }} of {{ totalItems }}
              </CCol>
            </CRow>

            <!-- Invoice Cards -->
            <CCard
              v-for="item in computedItems"
              :key="item.id"
              class="mb-3 border"
            >
              <CCardHeader class="d-flex justify-content-between align-items-center py-2 bg-light">
                <router-link :to="`/tenants/Invoice/${item.id}`" class="font-weight-bold text-monospace">
                  {{ item.invoiceNumber }}
                </router-link>
                <CBadge :color="badgeColor('invoice', item.statusDescription)">
                  {{ badgeLabel("invoice", item.statusDescription) }}
                </CBadge>
              </CCardHeader>
              <CCardBody class="py-2">
                <div class="d-flex justify-content-between mb-1">
                  <div class="text-muted small">Customer</div>
                  <div class="small">{{ item.customerName }}</div>
                </div>
                <div class="d-flex justify-content-between mb-1">
                  <div class="text-muted small">Date</div>
                  <div class="small">{{ item.displayIssuedDate }}</div>
                </div>

                <!-- Financial Breakdown -->
                <div class="mb-2 mt-2 pt-2 border-top">
                  <div class="d-flex justify-content-between mb-1">
                    <div class="text-muted small">Before tax</div>
                    <div class="small text-right">{{ formatCurrency(item.subTotal) }}</div>
                  </div>
                  <div class="d-flex justify-content-between mb-1">
                    <div class="text-muted small">Tax <span v-if="item.taxPercentage">({{ item.taxPercentage }}%)</span></div>
                    <div class="small text-right">{{ formatCurrency(item.taxAmount) }}</div>
                  </div>
                  <div class="d-flex justify-content-between font-weight-bold border-top pt-1">
                    <div class="small">Total</div>
                    <div class="small text-right">{{ formatCurrency(item.grandTotal) }}</div>
                  </div>
                </div>

                <div class="d-flex justify-content-between mb-1">
                  <div class="text-muted small">Payment</div>
                  <div>
                    <CBadge
                      v-if="payState(item)"
                      :color="badgeColor('documentPayment', payState(item))"
                      class="px-2 py-1"
                    >
                      {{ badgeLabel("documentPayment", payState(item)) }}
                    </CBadge>
                    <span v-else class="text-muted">—</span>
                  </div>
                </div>
                <div v-if="item.isPaid && item.paymentDate" class="d-flex justify-content-between mb-1">
                  <div class="text-muted small">Paid on</div>
                  <div class="small">{{ getDisplayDate(item.paymentDate) }}</div>
                </div>
                <div v-if="item.isPaid && item.paymentMethod" class="d-flex justify-content-between mb-1">
                  <div class="text-muted small">Method</div>
                  <div class="small">{{ item.paymentMethod }}</div>
                </div>
              </CCardBody>
              <CCardFooter class="py-2 d-flex justify-content-between bg-transparent">
                <CButton color="light" size="sm" @click="onEdit(item)">
                  <CIcon name="cil-pencil" /> Open
                </CButton>
                <CDropdown size="sm" color="light" placement="bottom-end" class="dropdown-mobile">
                  <template #toggler-content>
                    <CIcon name="cil-options" /> More
                  </template>
                  <CDropdownItem @click="onDuplicate(item)">
                    <CIcon name="cil-copy" class="mr-2" /> Duplicate
                  </CDropdownItem>
                  <CDropdownItem @click="onEdit(item)">
                    <CIcon name="cil-cloud-download" class="mr-2" /> Preview PDF
                  </CDropdownItem>
                  <CDropdownDivider />
                  <CDropdownItem @click="showDeleteConfirmation(item)" class="text-danger">
                    <CIcon name="cil-trash" class="mr-2" /> Delete
                  </CDropdownItem>
                </CDropdown>
              </CCardFooter>
            </CCard>

            <!-- Mobile Empty States: filtered vs first use -->
            <div v-if="computedItems.length === 0 && hasActiveFilters" class="text-center py-5">
              <CIcon name="cil-filter" size="2xl" class="text-secondary mb-3" />
              <h3 class="h5">No invoices match your filters</h3>
              <CButton color="light" class="mt-2" @click="resetFilters">
                <CIcon name="cil-x" /> Clear filters
              </CButton>
            </div>
            <div v-else-if="computedItems.length === 0" class="text-center py-5">
              <CIcon name="cil-inbox" size="2xl" class="text-secondary mb-3" />
              <h3 class="h5">No invoices yet</h3>
              <p class="text-muted mb-3">
                Create an invoice to bill a customer. It stays a draft until you approve it.
              </p>
              <CButton color="primary" @click="addNew">
                <CIcon name="cil-plus" /> Create invoice
              </CButton>
            </div>

            <!-- Mobile Pagination -->
            <CRow class="mt-3" v-if="computedItems.length > 0">
              <CCol xs="12" class="d-flex justify-content-center">
                <CPagination
                  :activePage="currentPage"
                  :pages="totalPages"
                  @update:activePage="onPageChange"
                  size="sm"
                />
              </CCol>
            </CRow>
          </div>
        </CCardBody>
      </CCard>
    </div>

    <!-- Delete Confirmation Modal -->
    <CModal
      :title="`Delete invoice ${itemToDelete.invoiceNumber || ''}?`"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
      @update:show="onDeleteConfirmation"
    >
      <p class="mb-0">
        Invoice <strong>{{ itemToDelete.invoiceNumber }}</strong>
        <template v-if="itemToDelete.customerName"> for {{ itemToDelete.customerName }}</template>
        <template v-if="itemToDelete.grandTotal != null"> ({{ formatCurrency(itemToDelete.grandTotal) }})</template>
        will be removed. This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false"> Keep it </CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          <CSpinner v-if="deleting" size="sm" class="mr-1" />
          Delete invoice
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import InvoiceApi from "@/lib/invoiceApi";
import CustomerApi from "@/lib/customerApi";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import InvoiceTrendChart from "../widgets/InvoiceTrendChart";
import InvoiceStatusChart from "../widgets/InvoiceStatusChart";
import { formatMyr } from "@/utils/money";
import { formatDate, mytToday, asDateString } from "@/utils/datetime";
import { badgeColor, badgeLabel, badgeChartColor, paymentState } from "@/utils/statusBadges";

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const pad2 = (n) => String(n).padStart(2, "0");
/** "yyyy-MM" key for the month `offset` months from today (Malaysia time). */
function monthKey(offset = 0) {
  const [y, m] = mytToday().split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + offset, 1));
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`;
}
/** First and last "yyyy-MM-dd" of a calendar month (monthIndex is 0-based, may overflow). */
function monthRange(year, monthIndex, months = 1) {
  const start = new Date(Date.UTC(year, monthIndex, 1));
  const end = new Date(Date.UTC(year, monthIndex + months, 0));
  const fmt = (d) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
  return { from: fmt(start), to: fmt(end) };
}

export default {
  name: "InvoiceList",
  components: {
    InvoiceTrendChart,
    InvoiceStatusChart,
    vSelect,
  },
  data() {
    return {
      loading: true,
      loadError: false,
      deleting: false,
      items: [],
      fields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:50px",
          sorter: false,
          filter: false,
        },
        {
          key: "invoiceNumber",
          label: "Invoice no.",
          sorter: true,
          filter: true
        },
        {
          key: "customerName",
          label: "Customer",
          sorter: true,
          filter: true
        },
        {
          key: "displayIssuedDate",
          label: "Issued",
          sorter: true,
          filter: true
        },
        {
          key: "subTotal",
          label: "Before tax",
          sorter: true,
          filter: false,
          _style: "text-align: right; min-width: 100px"
        },
        {
          key: "taxAmount",
          label: "Tax",
          sorter: true,
          filter: false,
          _style: "text-align: right; min-width: 80px"
        },
        {
          key: "grandTotal",
          label: "Total",
          sorter: true,
          filter: false,
          _style: "text-align: right; min-width: 100px"
        },
        {
          key: "show_status",
          label: "Status",
          sorter: true,
          filter: true
        },
        {
          key: "show_payment_status",
          label: "Payment",
          sorter: false,
          filter: false,
          _style: "width:120px"
        },
        {
          key: "show_details",
          label: "Actions",
          _style: "width:140px",
          sorter: false,
          filter: false,
        },
      ],
      details: [],
      collapseDuration: 0,
      api: new InvoiceApi(),
      customerApi: new CustomerApi(),
      warningModal: false,
      itemToDelete: {},

      // Pagination
      currentPage: 1,
      perPage: 10,
      totalItems: 0,
      totalPages: 1,

      // Sorting
      sortBy: 'issuedDate',
      sortDirection: 'desc',

      // Filters
      showFilters: false,
      filters: {
        invoiceNumber: "",
        customerId: "",
        status: "",
        dateFrom: "",
        dateTo: "",
        quickPeriod: "all", // The list loads all invoices by default
        selectedMonthYear: ""
      },
      selectedCustomerFilter: null,
      monthYearOptions: [],

      // Mobile Analytics
      mobileAnalyticsExpanded: false,

      // Status options from API
      statusOptions: [],

      // Customer options
      customerItems: [],

      // Real analytics source — all invoices for the org-wide summary
      // (separate one-time pull; the paginated table uses `items`)
      allInvoices: [],
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Sales", "Invoices"]);
    this.generateMonthYearOptions();
    this.loadStatusOptions();
    this.loadCustomers();
    this.refreshTable(); // Load all invoices by default (matches quickPeriod "all")
    this.fetchSummary(); // Load org-wide invoices for real analytics
  },
  computed: {
    computedItems() {
      return this.items.map((item) => {
        const taxBreakdown = this.calculateTaxBreakdown(item);
        return {
          ...item,
          displayIssuedDate: this.getDisplayDate(item.issuedDate),
          customerName: this.getCustomerName(item),
          subTotal: taxBreakdown.subTotal,
          taxAmount: taxBreakdown.taxAmount,
          taxPercentage: taxBreakdown.taxPercentage,
          grandTotal: taxBreakdown.grandTotal,
        };
      });
    },

    // Pagination computed properties
    paginationStart() {
      if (this.totalItems === 0) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },
    paginationEnd() {
      return Math.min(this.currentPage * this.perPage, this.totalItems);
    },

    // Filter tracking
    // Quick period / month pickers only set dateFrom/dateTo, so they are counted through those.
    activeFilterKeys() {
      const f = this.filters;
      return ["invoiceNumber", "customerId", "status", "dateFrom", "dateTo"].filter((k) => f[k] !== "" && f[k] != null);
    },
    hasActiveFilters() {
      return this.activeFilterKeys.length > 0;
    },
    activeFiltersCount() {
      return this.activeFilterKeys.length;
    },

    // Customer options for filter
    customerOptions() {
      return this.customerItems;
    },

    // Filtered invoice summary for the current view
    filteredSummary() {
      const invoices = this.computedItems;

      let subTotal = 0;
      let taxAmount = 0;
      let totalAmount = 0;
      let paidAmount = 0;
      let unpaidAmount = 0;
      let paidCount = 0;
      let unpaidCount = 0;

      invoices.forEach(invoice => {
        const invoiceSubTotal = parseFloat(invoice.subTotal) || 0;
        const invoiceTaxAmount = parseFloat(invoice.taxAmount) || 0;
        const amount = parseFloat(invoice.grandTotal) || 0;

        subTotal += invoiceSubTotal;
        taxAmount += invoiceTaxAmount;
        totalAmount += amount;

        // isPaid means fully paid (docs/ACCOUNTING-POSTING.md); a part-paid invoice counts its
        // payments as paid and only the balance as unpaid.
        if (invoice.isPaid) {
          paidAmount += amount;
          paidCount++;
        } else if (invoice.statusDescription === 'Approve') {
          const paidSoFar = Math.min(this.invPaid(invoice), amount);
          paidAmount += paidSoFar;
          unpaidAmount += amount - paidSoFar;
          unpaidCount++;
        }
      });

      const averageAmount = invoices.length > 0 ? totalAmount / invoices.length : 0;

      return {
        subTotal,
        taxAmount,
        totalAmount,
        paidAmount,
        unpaidAmount,
        paidCount,
        unpaidCount,
        averageAmount,
        totalCount: invoices.length
      };
    },

    // ===== Real analytics (computed from allInvoices) =====
    // Org-wide summary; keys preserved so desktop + mobile templates stay unchanged.
    invoiceSummary() {
      const all = this.allInvoices || [];
      let paidAmount = 0, paidCount = 0, pendingAmount = 0, pendingCount = 0, overdueAmount = 0, overdueCount = 0;
      all.filter((i) => i.statusDescription === "Approve").forEach((i) => {
        const amt = this.invAmount(i);
        const out = this.invOutstanding(i, amt);
        if (out <= 0.01) { paidAmount += amt; paidCount++; }
        else if (this.invOverdue(i)) { overdueAmount += out; overdueCount++; }
        else { pendingAmount += out; pendingCount++; }
      });
      const issuedIn = (key) => all.filter((i) => asDateString(i.issuedDate).slice(0, 7) === key).length;
      const cThis = issuedIn(monthKey(0)), cLast = issuedIn(monthKey(-1));
      const countChange = cLast ? Math.round(((cThis - cLast) / cLast) * 1000) / 10 : (cThis > 0 ? 100 : 0);
      return { totalCount: all.length, countChange, paidAmount, paidCount, pendingAmount, pendingCount, overdueAmount, overdueCount };
    },

    statusDistribution() {
      const s = this.invoiceSummary;
      const draftCount = (this.allInvoices || []).filter((i) => i.statusDescription === "Draft").length;
      const items = [
        { name: "Paid", type: "documentPayment", count: s.paidCount },
        { name: "Unpaid", type: "documentPayment", count: s.pendingCount },
        { name: "Overdue", type: "documentPayment", count: s.overdueCount },
        { name: "Draft", type: "invoice", count: draftCount },
      ].filter((x) => x.count > 0);
      const total = items.reduce((a, b) => a + b.count, 0) || 1;
      return items.map((x) => ({
        ...x,
        label: badgeLabel(x.type, x.name),
        color: badgeColor(x.type, x.name),
        chartColor: badgeChartColor(x.type, x.name),
        percentage: Math.round((x.count / total) * 1000) / 10,
      }));
    },

    // Chart data for invoice trends — last 6 months, Invoiced vs Paid (real)
    invoiceTrendData() {
      const months = [];
      for (let i = 5; i >= 0; i--) months.push(monthKey(-i));
      const all = this.allInvoices || [];
      const inMonth = (d, key) => asDateString(d).slice(0, 7) === key;
      const invoiced = months.map((m) =>
        all.filter((i) => i.statusDescription === "Approve" && inMonth(i.issuedDate, m))
          .reduce((a, i) => a + this.invAmount(i), 0)
      );
      const paid = months.map((m) => {
        let t = 0;
        all.forEach((i) => (i.payments || []).forEach((p) => { if (inMonth(p.paymentDate, m)) t += Number(p.amount) || 0; }));
        return t;
      });
      const invoicedColor = badgeChartColor("invoice", "Approve");
      const paidColor = badgeChartColor("documentPayment", "Paid");
      return {
        labels: months.map((m) => MONTH_SHORT[Number(m.slice(5, 7)) - 1]),
        datasets: [
          { label: "Invoiced", fill: false, backgroundColor: "transparent", borderColor: invoicedColor, pointBackgroundColor: invoicedColor, data: invoiced },
          { label: "Paid", fill: false, backgroundColor: "transparent", borderColor: paidColor, pointBackgroundColor: paidColor, data: paid },
        ],
      };
    },

    // Chart data for status distribution
    statusDistributionData() {
      return {
        labels: this.statusDistribution.map((status) => status.label),
        datasets: [
          {
            data: this.statusDistribution.map((status) => status.count),
            backgroundColor: this.statusDistribution.map((status) => status.chartColor),
          },
        ],
      };
    },
  },
  methods: {
    badgeColor,
    badgeLabel,

    // Payment state badge key for an approved invoice ("Paid" | "Partial" | "Overdue" | "Unpaid"); null for drafts.
    payState(item) {
      if (item.isPaid) return "Paid";
      if (item.statusDescription !== "Approve") return null;
      return paymentState({
        isPaid: false,
        paid: this.invPaid(item),
        total: this.invAmount(item),
        dueDate: asDateString(item.dueDate),
        today: mytToday(),
      });
    },

    refreshAll() {
      this.refreshTable();
      this.fetchSummary();
    },

    // Mobile Analytics Toggle
    toggleMobileAnalytics() {
      this.mobileAnalyticsExpanded = !this.mobileAnalyticsExpanded;
    },

    getDisplayDate(dt) {
      return formatDate(dt);
    },

    getCustomerName(item) {
      if (item.customer) {
        return item.customer.name;
      }
      return "N/A";
    },

    // Calculate tax breakdown for an invoice
    calculateTaxBreakdown(invoice) {
      // Calculate subtotal from all items
      let subtotal = 0;
      if (invoice.items && invoice.items.length > 0) {
        for (let i = 0; i < invoice.items.length; i++) {
          const item = invoice.items[i];
          subtotal += (item.price || 0) * (item.quantity || 0);
        }
      }

      // Get tax rate - try salesTax object first, then direct property
      const taxRate = invoice.salesTax?.rateInPercentage || invoice.taxRateInPercentage || 0;

      // Calculate tax amount
      const taxAmount = (taxRate * subtotal) / 100;

      // Return breakdown object
      return {
        subTotal: parseFloat(subtotal.toFixed(2)),
        taxAmount: parseFloat(taxAmount.toFixed(2)),
        taxPercentage: taxRate,
        grandTotal: parseFloat((subtotal + taxAmount).toFixed(2))
      };
    },

    getGrandTotal(invoice) {
      const breakdown = this.calculateTaxBreakdown(invoice);
      return breakdown.grandTotal.toFixed(2);
    },

    formatCurrency(amount) {
      return `RM ${formatMyr(amount)}`;
    },

    // ===== Analytics helpers (org-wide summary from allInvoices) =====
    fetchSummary() {
      this.api
        .getListByCurrentBusiness({ page: 1, pageSize: 1000 })
        .then((r) => {
          this.allInvoices = (r && (r.data || (r.result && (r.result.data || r.result)))) || [];
        })
        .catch(() => { /* analytics degrade gracefully; the table still works */ });
    },
    invAmount(i) {
      const g = Number(i.grandTotal) || 0;
      if (g > 0) return g;
      const t = Number(i.totalAmount) || 0;
      if (t > 0) return t;
      return this.calculateTaxBreakdown(i).grandTotal;
    },
    invPaid(i) {
      return (i.payments || []).reduce((a, p) => a + (Number(p.amount) || 0), 0);
    },
    invOutstanding(i, amt) {
      if (i.isPaid) return 0;
      const a = amt != null ? amt : this.invAmount(i);
      return Math.max(0, a - this.invPaid(i));
    },
    invOverdue(i) {
      return !i.isPaid && this.invOutstanding(i) > 0.01 && !!i.dueDate && asDateString(i.dueDate) < mytToday();
    },

    // Global toasts (src/plugins/toast.js)
    toast(header, message, color) {
      const kind = { danger: "error", success: "success", warning: "warning", info: "info" }[color] || "info";
      this.$toast[kind](message);
    },

    // Generate month/year dropdown options (last 24 months)
    generateMonthYearOptions() {
      const options = [];
      for (let i = 0; i < 24; i++) {
        const value = monthKey(-i);
        const label = `${MONTH_LONG[Number(value.slice(5, 7)) - 1]} ${value.slice(0, 4)}`;
        options.push({ value, label });
      }

      this.monthYearOptions = options;
    },

    // Apply quick period filter
    applyQuickFilter(period) {
      this.filters.quickPeriod = period;
      this.filters.selectedMonthYear = '';

      // Date-only strings in Malaysia time (never toISOString on local dates: it shifts by 8 hours)
      const [year, month] = mytToday().split("-").map(Number);
      const monthIndex = month - 1;
      let range = null;

      switch (period) {
        case 'thisMonth':
          range = monthRange(year, monthIndex);
          break;

        case 'lastMonth':
          range = monthRange(year, monthIndex - 1);
          break;

        case 'thisQuarter':
          range = monthRange(year, Math.floor(monthIndex / 3) * 3, 3);
          break;

        case 'thisYear':
          range = { from: `${year}-01-01`, to: `${year}-12-31` };
          break;

        case 'all':
        default:
          range = { from: '', to: '' };
      }
      this.filters.dateFrom = range.from;
      this.filters.dateTo = range.to;

      this.currentPage = 1;
      this.refreshTable();
    },

    // Apply specific month/year filter
    applyMonthYearFilter() {
      if (this.filters.selectedMonthYear) {
        const [year, month] = this.filters.selectedMonthYear.split('-');
        const yearNum = parseInt(year);
        const monthNum = parseInt(month);

        this.filters.quickPeriod = '';
        const range = monthRange(yearNum, monthNum - 1);
        this.filters.dateFrom = range.from;
        this.filters.dateTo = range.to;

        this.currentPage = 1;
        this.refreshTable();
      }
    },

    // Clear quick filter
    clearQuickFilter() {
      this.filters.quickPeriod = 'all';
      this.filters.selectedMonthYear = '';
      this.applyQuickFilter('all');
    },

    // Get quick period label for display
    getQuickPeriodLabel(period) {
      const labels = {
        'thisMonth': 'This month',
        'lastMonth': 'Last month',
        'thisQuarter': 'This quarter',
        'thisYear': 'This year',
        'all': 'All time'
      };
      return labels[period] || period;
    },

    refreshTable() {
      var self = this;
      self.loading = true;
      self.loadError = false;

      // Build params for API call
      const params = {
        page: self.currentPage,
        pageSize: self.perPage,
        sortBy: self.sortBy,
        sortDirection: self.sortDirection
      };

      // Add filters
      if (self.filters.invoiceNumber) {
        params.invoiceNumber = self.filters.invoiceNumber;
      }
      if (self.filters.customerId) {
        params.customerId = self.filters.customerId;
      }
      if (self.filters.status) {
        params.status = self.filters.status;
      }
      if (self.filters.dateFrom) {
        params.dateFrom = self.filters.dateFrom;
      }
      if (self.filters.dateTo) {
        params.dateTo = self.filters.dateTo;
      }


      self.api
        .getListByCurrentBusiness(params)
        .then((response) => {
          // Handle paginated API response
          if (response && response.data) {
            self.items = response.data || [];
            self.totalItems = response.totalRecords || 0;
            self.currentPage = response.page || 1;
            self.totalPages = response.totalPages || 1;
          } else if (response.result && response.result.data) {
            self.items = response.result.data || [];
            self.totalItems = response.result.totalRecords || 0;
            self.currentPage = response.result.page || 1;
            self.totalPages = response.result.totalPages || 1;
          } else {
            // Simple response (backward compatibility)
            self.items = response.result || response || [];
            self.totalItems = self.items.length;
            self.totalPages = Math.ceil(self.totalItems / self.perPage);
          }

          // Ensure totalPages is at least 1 if there are items
          if (self.totalItems > 0 && self.totalPages < 1) {
            self.totalPages = 1;
          }

          self.loading = false;
        })
        .catch((error) => {
          console.error('[InvoiceList] load failed:', error);
          self.loadError = true;
          self.items = [];
          self.totalItems = 0;
          self.totalPages = 1;
          self.loading = false;
        });
    },

    onEdit(item) {
      var self = this;
      self.$router.push({
        path: `/tenants/Invoice/${item.id}`,
      });
    },

    onDuplicate(item) {
      var self = this;
      self.$router.push({
        path: `/tenants/Invoice/${item.id}/duplicate`,
      });
    },

    showDeleteConfirmation(item) {
      var self = this;
      self.itemToDelete = item;
      self.warningModal = true;
    },

    confirmDelete() {
      if (this.deleting) return;
      this.deleting = true;
      const number = this.itemToDelete.invoiceNumber;
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.refreshAll();
          this.$toast.success(number ? `Invoice ${number} deleted` : "Invoice deleted");
          this.warningModal = false;
        })
        .catch((error) => {
          console.error("[InvoiceList] delete failed:", error);
          this.$toast.error(`Couldn't delete the invoice. ${helper.getErrorMessage(error && error.data)}`);
        })
        .finally(() => {
          this.deleting = false;
        });
    },

    onDeleteConfirmation(status, evt, accept) {
      if (!status) {
        this.itemToDelete = {};
      }
    },

    addNew() {
      this.$router.push({ path: "/tenants/Invoice" });
    },

    // Pagination event handlers
    onPaginationChange(paginationData) {
      if (paginationData !== this.perPage) {
        this.perPage = parseInt(paginationData);
        this.currentPage = 1;
        this.refreshTable();
      }
    },

    onPageChange(page) {
      if (page !== this.currentPage) {
        this.currentPage = page;
        this.refreshTable();
      }
    },

    onItemsPerPageChange(itemsPerPage) {
      if (itemsPerPage !== this.perPage) {
        this.perPage = parseInt(itemsPerPage);
        this.currentPage = 1;
        this.refreshTable();
      }
    },

    // Sorting event handler
    onSorterChange(sorter) {
      if (sorter && sorter.column) {
        this.sortBy = sorter.column;
        this.sortDirection = sorter.asc ? 'asc' : 'desc';
        this.currentPage = 1;
        this.refreshTable();
      }
    },

    // Filter methods
    resetFilters() {
      this.filters = {
        invoiceNumber: "",
        customerId: "",
        status: "",
        dateFrom: "",
        dateTo: "",
        quickPeriod: "all",
        selectedMonthYear: "",
      };
      this.selectedCustomerFilter = null;
      this.currentPage = 1;
      this.refreshTable();
    },

    applyFilters() {
      this.currentPage = 1;
      this.refreshTable();
      // Don't auto-hide filters on mobile for better UX
      if (window.innerWidth >= 768) {
        this.showFilters = false;
      }
    },

    // Load status options from API
    loadStatusOptions() {
      var self = this;
      self.api
        .getInvoiceStatus()
        .then((response) => {
          self.statusOptions = response.result;
        })
        .catch((error) => {
          console.error("Error loading status options:", error);
          // Fallback to static options if API fails
          self.statusOptions = [
            { id: 0, name: "Draft" },
            { id: 1, name: "Approve" }
          ];
        });
    },

    // Load customers for filter
    loadCustomers() {
      var self = this;
      self.customerApi
        .getListByCurrentBusiness()
        .then((response) => {
          self.customerItems = response.result || [];
        })
        .catch((error) => {
          console.error("Error loading customers:", error);
          self.customerItems = [];
        });
    },

    // Handle customer filter selection
    onCustomerFilterChange(customer) {
      if (customer) {
        this.filters.customerId = customer.id;
      } else {
        this.filters.customerId = "";
      }
      this.applyFilters();
    },
  },
};
</script>

<style scoped>
/* Fix dropdown z-index issues */
.dropdown-with-zindex {
  position: static;
}

.dropdown-with-zindex .dropdown-menu {
  z-index: 1050 !important;
  position: absolute;
  will-change: transform;
}

.dropdown-mobile .dropdown-menu {
  z-index: 1055 !important;
  position: absolute;
  will-change: transform;
}

/* Enhanced action buttons.
   IMPORTANT: do NOT give the per-row action area its own z-index — that
   creates a stacking context per row and traps an open dropdown menu
   behind the action buttons of the rows below it. Keep z-index: auto so
   the absolutely-positioned menu (z-index 1050) wins against sibling rows. */
.action-buttons,
.action-buttons .btn-group {
  position: relative;
  z-index: auto;
}

/* Belt-and-suspenders: when a row's dropdown is open, lift that row's
   action area above its neighbours so the menu always renders on top. */
.action-buttons:has(.dropdown.show),
.action-buttons .btn-group:has(.dropdown.show) {
  z-index: 1060;
}

.action-buttons .dropdown-toggle::after {
  margin-left: 0.5em;
}

/* Clickable invoice numbers */
.cursor-pointer {
  cursor: pointer;
  transition: color 0.15s ease-in-out;
}

.cursor-pointer:hover {
  text-decoration: underline;
  color: #1e3a5f !important;
}

/* Loading spinner center alignment */
.spinner-border {
  width: 2rem;
  height: 2rem;
}

/* Badge improvements */
.badge {
  font-size: 0.75em;
  font-weight: 500;
  letter-spacing: 0.025em;
}

/* Better spacing for mobile pagination */
.pagination-sm .page-link {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

/* Improve mobile responsiveness */
@media (max-width: 575.98px) {
  .btn-group {
    flex-wrap: wrap;
  }

  .btn-group .btn {
    margin-bottom: 0.25rem;
  }
}

/* Custom styling for mobile cards */
@media (max-width: 991.98px) {
  .d-none.d-lg-block {
    display: none !important;
  }
}

@media (min-width: 992px) {
  .d-lg-none {
    display: none !important;
  }
}

/* Improve dropdown menu appearance */
.dropdown-menu {
  border: 1px solid #dee2e6;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.175);
  border-radius: 0.375rem;
}

.dropdown-item {
  padding: 0.5rem 1rem;
  transition: background-color 0.15s ease-in-out;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item.text-danger:hover {
  background-color: #f8d7da;
}

/* Button group improvements */
.btn-group .btn {
  border-color: #dee2e6;
}

.btn-group .btn:hover {
  border-color: #adb5bd;
  z-index: 1;
}

/* v-select styling for filters */
.v-select {
  width: 100%;
}

.v-select .vs__dropdown-toggle {
  border: 1px solid #d8dbe0;
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  min-height: 38px;
}

.v-select.vs--open .vs__dropdown-toggle {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.v-select .vs__search {
  margin: 0;
  padding: 4px 0;
}

.v-select .vs__selected {
  margin: 0;
  padding: 0;
}

.v-select .vs__dropdown-menu {
  border: 1px solid #d8dbe0;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.175);
  border-radius: 0.25rem;
  max-height: 250px;
}

.v-select .vs__dropdown-option {
  padding: 8px 12px;
}

.v-select .vs__dropdown-option--highlight {
  background-color: #f8f9fa;
  color: #16181b;
}

.v-select .vs__dropdown-option--selected {
  background-color: #007bff;
  color: #fff;
}

.v-select .vs__clear {
  margin-right: 4px;
}

/* Cursor pointer for clickable elements */
.cursor-pointer {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.cursor-pointer:hover {
  background-color: #f8f9fa !important;
}

.cursor-pointer:active {
  background-color: #e9ecef !important;
}

/* Mobile-specific optimizations */
@media (max-width: 991.98px) {
  /* Improve touch targets */
  .btn-sm {
    min-height: 38px;
    padding: 0.5rem 0.75rem;
  }

  /* Compact callouts for mobile */
  .c-callout {
    padding: 0.75rem !important;
  }

  /* Better spacing for mobile cards */
  .card-body {
    padding: 1rem !important;
  }

  /* Ensure tabs are hidden on mobile */
  .nav-tabs {
    display: none;
  }

  /* Improved quick stats readability */
  .quick-stats-mobile {
    font-size: 0.85rem;
  }
}

/* Tab styling improvements */
.nav-tabs .nav-link {
  font-weight: 600;
  color: #64748b;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 0.75rem 1.5rem;
  transition: color 0.2s ease, border-color 0.2s ease;
}

.nav-tabs .nav-link.active {
  color: #1e3a5f;
  border-bottom-color: #10b981;
  background-color: transparent;
}

.nav-tabs .nav-link:hover {
  color: #1e3a5f;
  border-bottom-color: #10b981;
}

.nav-tabs {
  border-bottom: 1px solid #d8dbe0;
  margin-bottom: 1.5rem;
}

/* Badge in tab improvements */
.nav-link .badge {
  margin-left: 0.5rem;
  font-size: 0.7em;
  font-weight: 600;
  padding: 0.25em 0.6em;
}

/* Mobile analytics collapse — height handled by CCollapse (avoid layout-thrash transition) */

/* Sticky analytics header on mobile (optional enhancement) */
@media (max-width: 991.98px) {
  .mobile-analytics-header {
    position: sticky;
    top: 0;
    z-index: 100;
    background: white;
  }
}

/* ============================================================= */
/* Brand redesign — navy #1E3A5F / emerald #10B981               */
/* ============================================================= */
h1 { color: #1e3a5f; }

/* Buttons → brand */
::v-deep .btn-primary {
  background-color: #10b981;
  border-color: #10b981;
}
::v-deep .btn-primary:hover,
::v-deep .btn-primary:focus,
::v-deep .btn-primary:not(:disabled):not(.disabled):active {
  background-color: #059669;
  border-color: #059669;
}
::v-deep .btn-outline-primary {
  color: #1e3a5f;
  border-color: #1e3a5f;
}
::v-deep .btn-outline-primary:hover,
::v-deep .btn-outline-primary.active,
::v-deep .btn-outline-primary:not(:disabled):not(.disabled).active {
  background-color: #1e3a5f;
  border-color: #1e3a5f;
  color: #fff;
}
::v-deep .badge-success { background-color: #10b981; }

/* KPI tiles */
.inv-kpi {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1.1rem 1.15rem;
  height: 100%;
}
.inv-kpi-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem; }
.inv-kpi-label { font-size: 0.8rem; font-weight: 600; color: #64748b; }
.inv-kpi-chip { width: 34px; height: 34px; border-radius: 9px; display: grid; place-items: center; font-size: 0.95rem; }
.chip-navy { background: rgba(30, 58, 95, 0.1); color: #1e3a5f; }
.chip-success { background: rgba(16, 185, 129, 0.13); color: #059669; }
.chip-warning { background: rgba(245, 158, 11, 0.14); color: #b45309; }
.chip-danger { background: rgba(239, 68, 68, 0.11); color: #ef4444; }
.inv-kpi-value { font-size: 1.5rem; font-weight: 800; color: #1e3a5f; line-height: 1.1; letter-spacing: -0.02em; }
.inv-kpi-sub { font-size: 0.74rem; color: #94a3b8; margin-top: 0.35rem; }
.inv-kpi-sub.trend-up { color: #059669; }
.inv-kpi-sub.trend-down { color: #ef4444; }

/* Filtered Results Summary */
.inv-summary-card { border: 1px solid #e2e8f0; border-radius: 14px; box-shadow: none; }
.inv-summary-title { font-weight: 700; color: #1e3a5f; }
.badge-soft { background: #eef2f7; color: #1e3a5f; font-weight: 600; }
.inv-stat { background: #f8fafc; border: 1px solid #eef2f7; border-radius: 10px; padding: 0.6rem 0.7rem; height: 100%; }
.inv-stat-mini { padding: 0.15rem; }
.inv-stat-label { font-size: 0.75rem; color: #64748b; }
.inv-stat-val { font-size: 0.95rem; font-weight: 700; color: #334155; }
.inv-stat-meta { font-size: 0.66rem; color: #94a3b8; }
.text-brand-navy { color: #1e3a5f !important; }
.text-success-strong { color: #059669 !important; }
.text-danger-strong { color: #ef4444 !important; }
.text-warn { color: #b45309 !important; }

/* Table polish */
::v-deep .table thead th {
  color: #64748b;
  border-top: none;
  border-bottom: 1px solid #e2e8f0;
}
::v-deep .table tbody td { vertical-align: middle; }
::v-deep .table.table-hover tbody tr:hover { background: #f8fafc; }
</style>
